import { useInputEvent } from '@conform-to/react'
import React, { type JSX, useId, useRef } from 'react'
import { cn } from '#app/utils/misc.tsx'
import { Checkbox, type CheckboxProps } from './ui/checkbox.tsx'
import { Input } from './ui/input.tsx'
import { Label } from './ui/label.tsx'
import { RadioGroup, RadioGroupItem, type RadioGroupProps } from './ui/radio-group.tsx'
import { Textarea, type TextareaProps } from './ui/textarea.tsx'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
	elementClassName,
}: {
	errors?: ListOfErrors
	id?: string
	elementClassName?: React.HTMLAttributes<HTMLLIElement>['className']
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map(e => (
				<li key={e} className={cn('text-sm text-foreground-destructive', elementClassName)}>
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
	noErrors,
	suggestions,
	helpMessage,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement> & { icon?: React.ReactNode }
	errors?: ListOfErrors
	className?: string
	noErrors?: boolean
	suggestions?: string[]
	helpMessage?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				list={suggestions?.length ? `${id}-suggestions` : undefined}
				{...inputProps}
			/>
			{helpMessage && <span className="text-sm font-light text-neutral-500">{helpMessage}</span>}
			{noErrors ? null : (
				<div className="min-h-[32px] px-4 pb-3 pt-1">{errorId ? <ErrorList id={errorId} errors={errors} /> : null}</div>
			)}
			{suggestions?.length ? (
				<datalist id={`${id}-suggestions`}>
					{suggestions.map(suggestion => (
						<option key={suggestion} value={suggestion} />
					))}
				</datalist>
			) : null}
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: TextareaProps
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea id={id} aria-invalid={errorId ? true : undefined} aria-describedby={errorId} {...textareaProps} />
			<div className="min-h-[32px] px-4 pb-3 pt-1">{errorId ? <ErrorList id={errorId} errors={errors} /> : null}</div>
		</div>
	)
}

export function UnstyledTextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<textarea id={id} aria-invalid={errorId ? true : undefined} aria-describedby={errorId} {...textareaProps} />
			<div className="min-h-[32px] px-4 pb-3 pt-1">{errorId ? <ErrorList id={errorId} errors={errors} /> : null}</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const buttonRef = useRef<HTMLButtonElement>(null)
	// To emulate native events that Conform listen to:
	// See https://conform.guide/integrations
	const control = useInputEvent({
		// Retrieve the checkbox element by name instead as Radix does not expose the internal checkbox element
		// See https://github.com/radix-ui/primitives/discussions/874
		ref: () => buttonRef.current?.form?.elements.namedItem(buttonProps.name ?? ''),
		onFocus: () => buttonRef.current?.focus(),
	})
	const id = buttonProps.id ?? buttonProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					id={id}
					ref={buttonRef}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					{...buttonProps}
					onCheckedChange={state => {
						control.change(Boolean(state.valueOf()))
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={event => {
						control.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={event => {
						control.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label htmlFor={id} {...labelProps} className="cursor-pointer self-center text-body-xs text-muted-foreground" />
			</div>
			<div className="px-4 pb-3 pt-1">{errorId ? <ErrorList id={errorId} errors={errors} /> : null}</div>
		</div>
	)
}

export type RadioGroupOption = {
	value: string
	label: string
}
export function RadioGroupField({
	labelProps,
	radioGroupProps,
	errors,
	className,
	noErrors,
	options,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	radioGroupProps: RadioGroupProps
	errors?: ListOfErrors
	className?: HTMLDivElement['className']
	noErrors?: boolean
	options: Array<
		RadioGroupOption & {
			render?: ({ value, label }: RadioGroupOption) => React.ReactNode
		}
	>
}) {
	const fallbackId = useId()
	const id = radioGroupProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<Label {...labelProps} htmlFor={id} className={cn('pb-2', labelProps.className)}>
				{labelProps.children}
			</Label>
			<RadioGroup {...radioGroupProps} id={id}>
				{options.map(option =>
					option.render ? (
						option.render(option)
					) : (
						<div key={option.value} className="flex items-center space-x-2">
							<RadioGroupItem value={option.value} id={option.value} />
							<Label htmlFor={option.value}>{option.label}</Label>
						</div>
					),
				)}
			</RadioGroup>
			{noErrors ? null : (
				<div className="min-h-[32px] px-4 pb-3 pt-1">{errorId ? <ErrorList id={errorId} errors={errors} /> : null}</div>
			)}
		</div>
	)
}
